import { useEffect, useState } from "react";
import { Droplet } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

/**
 * 8-glass hydration row. Tap a glass to fill to that count; tap the last
 * filled glass to un-fill it. Optimistic UI; server is authoritative on
 * reload.
 */
export default function HydrationRow() {
  const [glasses, setGlasses] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const goal = 8;

  useEffect(() => {
    apiClient
      .get("/hydration/today")
      .then((r) => setGlasses(r.data?.glasses ?? 0))
      .catch(() => setGlasses(0))
      .finally(() => setLoaded(true));
  }, []);

  const setTo = async (count) => {
    // Optimistic
    const prev = glasses;
    setGlasses(count);
    try {
      await apiClient.post("/hydration/log", { glasses: count });
    } catch {
      setGlasses(prev);
      toast.error("Could not log");
    }
  };

  const tap = (idx) => {
    // idx is 0-based. Filled if idx < glasses.
    if (idx + 1 === glasses) {
      // Un-fill the last filled glass.
      setTo(idx);
    } else {
      // Fill up to this glass.
      setTo(idx + 1);
    }
  };

  return (
    <section
      data-testid="hydration-row"
      style={{
        background: "#FFFFFF",
        border: "1px solid var(--border, rgba(12,12,11,0.08))",
        borderRadius: 16,
        padding: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div style={{ fontFamily: "Barlow, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text-primary,#0C0C0B)" }}>
          Hydration
        </div>
        <div
          data-testid="hydration-count"
          style={{ fontFamily: "Barlow, sans-serif", fontSize: 11, color: "var(--text-tertiary,#8A8884)" }}
        >
          {loaded ? `${glasses}/${goal} glasses` : "…"}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6 }}>
        {Array.from({ length: goal }).map((_, i) => {
          const filled = i < glasses;
          return (
            <button
              key={i}
              onClick={() => tap(i)}
              data-testid={`hydration-glass-${i}`}
              aria-label={`glass ${i + 1}`}
              style={{
                aspectRatio: "1",
                border: "1px solid " + (filled ? "#00D4FF" : "var(--border, rgba(12,12,11,0.14))"),
                background: filled ? "rgba(0,212,255,0.16)" : "transparent",
                color: filled ? "#0087A8" : "var(--text-tertiary,#8A8884)",
                borderRadius: 10,
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <Droplet size={14} fill={filled ? "#00D4FF" : "transparent"} />
            </button>
          );
        })}
      </div>
    </section>
  );
}
